import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Icon from '@mdi/react';
import chevronRight from '@mdi/svg/svg/chevron-right.svg';
import { NavLink } from 'react-router-dom';
import { BaseButton, forPhoneOnly, Tooltip, Variables } from '../base';
import { DataTable, InfiniteScrollDataTable } from '../data-table';
import { 
    TableContainer as DataTableContainer,
    TableHead as DataTableHead,
    TableHeader as DataTableHeader,
    TableBody as DataTableBody,
    TableRow as DataTableRow,
    TableCell as DataTableCell,
} from '../data-table/infinite-scroll-data-table.styles';
import { AttachmentCount } from './attachment-count';
import { AuthorIndicator } from './author-indicator';
import { sidePanelWidth } from './side-panel.styles';

interface IUnread {
    unread: boolean;
}

interface IOpen {
    open: boolean;
}

const stylesIfUnread = (props: IUnread) => css`
    font-weight: ${props.unread ? '500' : 'normal'};
`;

interface IControlProps {
    active: boolean;
}

export const MessageList = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
`;

export const MessageControls = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    flex-grow: 0;
    padding: 0 16px;
    flex-wrap: wrap;
    justify-content: space-between;

    ${forPhoneOnly(`
        flex-direction: column;
    `)};
`;

export const MessageControl = styled.div`
    display: flex;
`;

export const TableContainer = styled.div`
    flex-grow: 1;

    overflow: auto;

    /* for Firefox */
    min-height: 0;
`;

export const MessageTable = styled(DataTable)`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    align-self: stretch;
    max-height: 100%;

    & [role='columnheader'] {
        display: inline-flex;
    }

    & [role='cell'] {
        white-space: nowrap;
        overflow-x: hidden;
        text-overflow: ellipsis;
        padding: 16px;
    }

    & [role='row']:hover {
        background-color: ${Variables.Colors.n15_24};
        cursor: pointer;
    }
`;

export const IndicatorField = styled(AuthorIndicator)`
    margin-right: 8px;
    &:not(:first-of-type) {
        margin-left: 8px;
    }
`;

export const FromField = styled.div<IUnread>`
    display: flex;
    align-items: center;
    margin-right: 8px;
    ${stylesIfUnread}
`;

export const AttachmentField = styled(AttachmentCount)`
    margin-left: 8px;
`;

export const SubjectField = styled.div<IUnread>`
    display: flex;
    align-items: center;
    flex-grow: 1;
    ${stylesIfUnread}
`;

export const ActionField = styled.div`
    padding-right: 12px;
    color: ${Variables.Colors.n15};
    svg {
        fill: ${Variables.Colors.n15};
    }
`;

export const Spacer = styled.div`
    flex-grow: 1;
`;

export const ControlCount = styled.span`
    margin-left: 12px;
`;

export const ControlButton = styled(BaseButton)<IControlProps>`
    align-self: center;
    border: none;

    padding: 0 12px;
    height: 28px;

    font-size: 20px;
    font-weight: 500;
    color: ${(props: IControlProps): string => (props.active ? Variables.Colors.n70 : Variables.Colors.n15)};
    background-color: ${(props: IControlProps): string =>
        props.active ? Variables.Colors.ghostWhite : Variables.Colors.n15_24};

    &:not(:first-of-type) {
        margin-left: 32px;
        ${forPhoneOnly(`
            margin: 0;
        `)}
    }

    & ${ControlCount} {
        color: ${(props: IControlProps): string => (props.active ? Variables.Colors.n50 : Variables.Colors.n15)};
    }
`;

export const ComposeButton = styled(BaseButton)`
    align-self: flex-end;
    padding: 0 11px 0 7px;

    ${forPhoneOnly(`
        margin-top: 20px;
    `)};
`;

export const ComposeIcon = styled(Icon)`
    fill: white;
`;
export const ComposeLabel = styled.span``;

export const UnreadButton = styled(BaseButton)`
    align-self: flex-end;
    padding: 0 11px 0 7px;
    margin-left: 16px;

    color: ${Variables.Colors.b300};
    background-color: ${Variables.Colors.ghostWhite};
    border: none;
    &:hover {
        background-color: ${Variables.Colors.b300_l96};
    }
    &:active {
        background-color: ${Variables.Colors.b300_l90};
    }
`;

const openMessage = `
    color: ${Variables.Colors.n70};
`;

const doneMessage = `
    color: ${Variables.Colors.n50};
    font-style: italic;
`;

export const MessageLink = styled(NavLink)<IOpen>`
    display: inline-flex;
    flex-grow: 1;
    align-items: baseline;
    padding: 16px;
    line-height: 22px;

    ${forPhoneOnly(`
        padding: 12px 8px;
    `)};

    ${(props: IOpen): string => (props.open ? openMessage : doneMessage)}
`;

const selectedOffset = '50px';
export const SelectedMessageRow = styled.div<{ show: boolean }>`
    display: ${({ show }) => (show ? 'block' : 'none')};
    position: relative;
    z-index: 10;
    background-color: ${Variables.Swatches.AlphaMiddleGray40};
    box-shadow: 0 8px 12px 0 ${Variables.Colors.nx4}, 0 -4px 12px 0 ${Variables.Colors.nx4},
        0 -0.5px 0 0 ${Variables.Colors.n15}, 0 0.5px 0 0 ${Variables.Colors.n15} !important;
    clip-path: polygon(
        0 -100%,
        calc(100% - ${sidePanelWidth} + ${selectedOffset}) -100%,
        calc(100% - ${sidePanelWidth} + ${selectedOffset}) 200%,
        0 200%
    );
    animation: 0.6s fadein;

    @keyframes fadein {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    &:after {
        animation: 0.6s fadein;
        content: '';
        position: absolute;
        right: calc(${sidePanelWidth} - ${selectedOffset});
        height: 100%;
        width: 48px;
        background: ${Variables.Swatches.AlphaMiddleGray40};
        background: linear-gradient(90deg, rgba(217, 221, 227, 0.8) 0%, rgba(217, 221, 227, 1) 25%);
    }
    &:before {
        animation: 0.6s fadein;
        z-index: 11;
        content: '';
        height: 24px;
        width: 24px;
        mask: url(${chevronRight});
        mask-size: initial;
        position: absolute;
        right: calc(${sidePanelWidth} - ${selectedOffset});
        margin-top: 15px;
        background-color: ${Variables.Colors.b300};
    }
    &:hover {
        background-color: ${Variables.Swatches.AlphaMiddleGray40} !important;
    }
`;

export const MessageListPanel = styled(MessageList)`
    padding: 10px;
    max-height: 100%;
    ${ComposeButton} {
        width: 100%;
    }
`;

export const CloseButton = styled(BaseButton)`
    border: 0;
    min-width: 0;
    padding: 0 8px;
    margin-left: 8px;
`;

export const ReplyIcon = styled(Icon)`
    position: relative;
    top: 4px;
    fill: ${Variables.Colors.n30};
`;

export const CommentCount = styled.span`
    color: ${Variables.Colors.n30};
`;

export const CloseIcon = styled(Icon)`
    fill: ${Variables.Colors.n30};
`;

export const Header = styled.section`
    display: flex;
`;

export const TabsContainer = styled.div`
    display: flex;
`;

export const TabsDivider = styled.div`
    border-top: 1px solid ${Variables.Colors.n15};
    margin-top: 16px;
    margin-bottom: 0;
`;

export const TabsControlWrapper = styled(MessageControl)`
    margin-top: 16px;
    margin-bottom: 16px;
`;

export const TabButton = styled(BaseButton)`
    /* Override disabled state to keep it clickable (selected tab is disabled) */
    &:disabled {
        opacity: 1;
        cursor: pointer;
        pointer-events: auto; /* Override base button's pointer-events: none */
    }
    
    & ${ControlCount} {
        color: inherit;
    }
`;

export const ControlsWrapper = styled.div`
    margin-right: 16px;
`;

export const FilterButton = styled(BaseButton)`
    white-space: nowrap;
    margin: 4px 16px 4px 0;
    /* width: 50%; */
    border: 1px solid ${Variables.Colors.n15};
    & > svg {
        margin-left: 8px;
    }
`;

export const MessageListContainer = styled.div`
    background: ${Variables.Swatches.Gray2};
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    border-radius: ${Variables.Rounded.medium};
`;

export const MessageHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 32px 32px 0;
`;

export const HeaderTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export const Title = styled.h1`
    font-size: 30px;
    font-weight: bold;
    color: #1e293b;
    margin: 0;
    line-height: 36px;
`;

export const HeaderActions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

export const IconButton = styled(BaseButton)`
    padding: 6px 8px;
    border-radius: 6px;
    border: none;
    background: transparent;
    min-width: auto;
    color: ${Variables.Swatches.AlphaDarkGray80};
    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }
`;

export const PrimaryButton = styled(BaseButton)`
    padding: 8px 16px;
    border-radius: 8px;
    background: #2563eb;
    color: white;
    font-weight: 600;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    border: none;
    &:hover {
        background: #1d4ed8;
        color: white !important;
        
        svg, path {
            fill: white !important;
        }
        
        * {
            color: white !important;
        }
    }
`;

export const TableCard = styled.div`
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    margin: 16px 32px 32px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

export const TableTabSection = styled.div`
    // padding: 16px 16px 0 16px;
    display: flex;
    align-items: flex-start;
    background-color: ${Variables.ThemeLight.BackgroundB1};
`;

export const TableHeaderSection = styled.div`
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    flex-wrap: wrap;
`;

export const SearchSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 250px;
`;

export const RecordCount = styled.div`
    font-size: 14px;
    color: #475569;
    font-weight: 500;
`;

export const SearchAndFilters = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

export const SearchInputWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 448px;
    border-radius: 4px;
`;

export const SearchIcon = styled.div`
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const SearchInput = styled.input`
    width: 100%;
    padding: 10px 16px 10px 30px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    font-size: 14px;
    background: white;
    &::placeholder {
        color: #9ca3af;
    }
    &:focus {
        outline: none;
        border-color: #2563eb;
    }
`;

export const FilterButtons = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
`;

export const ToolbarActions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
`;

export const ReadFilterSelectWrapper = styled.div`
    width: 200px;
    min-width: 200px;
`;

export const InboxButton = styled(BaseButton)<{ active: boolean }>`
    padding: 4px 6px;
    height: 32px;
    min-height: 32px;
    border: ${props => props.active ? 'none' : `1px solid ${Variables.Swatches.AlphaMiddleGray40}`};
    border-radius: 4px;
    background: ${props => props.active ? Variables.Swatches.AlphaMiddleGray40 : Variables.Swatches.White};
    color: ${props => props.active ? Variables.Swatches.Blue11 : Variables.Swatches.Gray8};
    font-family: 'Calibre', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 500;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 4px;
    box-shadow: none;
    min-width: auto;
    line-height: normal;
    
    /* Icon color */
    svg, path {
        fill: ${props => props.active ? Variables.Swatches.Blue11 : Variables.Swatches.Gray7};
    }
    
    /* Hover state */
    &:hover {
        background: ${props => props.active 
            ? Variables.Swatches.AlphaMiddleGray50
            : 'rgba(109, 126, 148, 0.05)'};
        border-color: ${props => props.active ? 'transparent' : Variables.Swatches.AlphaMiddleGray60};
        color: ${props => props.active ? Variables.Swatches.Blue11 : Variables.Swatches.Gray8};
        
        svg, path {
            fill: ${props => props.active ? Variables.Swatches.Blue11 : Variables.Swatches.Gray7};
        }
    }
    
    /* Active/Pressed state */
    &:active {
        background: ${props => props.active 
            ? Variables.Swatches.AlphaMiddleGray60
            : Variables.Swatches.AlphaMiddleGray10};
        transform: scale(0.98);
        transition: transform 0.1s ease;
    }
    
    /* Focus state */
    &:focus-visible {
        outline: 2px solid ${Variables.Swatches.AlphaMiddleGray40};
        outline-offset: 2px;
    }
`;

export const UnreadFilterButton = styled(BaseButton)<{ active?: boolean }>`
    padding: 4px 6px;
    height: 32px;
    min-height: 32px;
    border: ${props => props.active ? 'none' : `1px solid ${Variables.Swatches.AlphaMiddleGray40}`};
    border-radius: 4px;
    background: ${props => props.active ? Variables.Swatches.AlphaMiddleGray40 : Variables.Swatches.White};
    color: ${props => props.active ? Variables.Swatches.Blue11 : Variables.Swatches.Gray8};
    font-family: 'Calibre', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 500;
    font-size: 14px;
    box-shadow: none;
    min-width: auto;
    line-height: normal;
    
    /* Hover state */
    &:hover {
        background: ${props => props.active 
            ? Variables.Swatches.AlphaMiddleGray50
            : 'rgba(109, 126, 148, 0.05)'};
        border-color: ${props => props.active ? 'transparent' : Variables.Swatches.AlphaMiddleGray60};
        color: ${props => props.active ? Variables.Swatches.Blue11 : Variables.Swatches.Gray8};
    }
    
    /* Active/Pressed state */
    &:active {
        background: ${props => props.active 
            ? Variables.Swatches.AlphaMiddleGray60
            : Variables.Swatches.AlphaMiddleGray10};
        transform: scale(0.98);
        transition: transform 0.1s ease;
    }
    
    /* Focus state */
    &:focus-visible {
        outline: 2px solid ${Variables.Swatches.AlphaMiddleGray40};
        outline-offset: 2px;
    }
`;

export const PriorityBadge = styled.span<{ priority: string }>`
    padding: 3.5px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    ${props => {
        const p = props.priority?.toUpperCase();
        switch (p) {
            case 'URGENT':
                return 'background: #fee2e2; color: #991b1b;';
            case 'HIGH':
                return 'background: #fed7aa; color: #c2410c;';
            case 'NORMAL':
                return 'background: #dbeafe; color: #1e40af;';
            case 'LOW':
                return 'background: #dcfce7; color: #166534;';
            default:
                return 'background: #f1f5f9; color: #64748b;';
        }
    }}
`;

export const StatusBadge = styled.span`
    padding: 3.5px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
    background: #f1f5f9;
    color: #334155;
    display: inline-block;
`;

export const StatusIconsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const StatusIcon = styled.div<{ color?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
        fill: ${props => props.color || '#64748b'};
    }
`;

export const PriorityIconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

interface IPriorityTagProps {
    priority: string;
}

export const PriorityTag = styled.div<IPriorityTagProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px 8px;
    gap: 4px;
    height: 20px;
    border-radius: 32px;
    width: fit-content;
    
    /* Priority-specific colors */
    ${props => {
        const p = props.priority?.toUpperCase();
        switch (p) {
            case 'URGENT':
                return `
                    background: ${Variables.Swatches.Red4};
                    color: ${Variables.Swatches.Red9};
                    svg path {
                        fill: ${Variables.Swatches.Red9};
                    }
                `;
            case 'HIGH':
                return `
                    background: ${Variables.Swatches.Orange3};
                    color: ${Variables.Swatches.Orange8};
                    svg path {
                        fill: ${Variables.Swatches.Orange8};
                    }
                `;
            case 'NORMAL':
                return `
                    background: ${Variables.Swatches.Blue2};
                    color: ${Variables.Swatches.Blue8};
                    svg path {
                        fill: ${Variables.Swatches.Blue8};
                    }
                `;
            case 'LOW':
                return `
                    background: ${Variables.Swatches.Green2};
                    color: ${Variables.Swatches.Green9};
                    svg path {
                        fill: ${Variables.Swatches.Green9};
                    }
                `;
            default:
                return `
                    background: ${Variables.Swatches.Gray2};
                    color: ${Variables.Swatches.Gray6};
                    svg path {
                        fill: ${Variables.Swatches.Gray6};
                    }
                `;
        }
    }}
    
    font-family: 'Calibre', -apple-system, BlinkMacSystemFont, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
`;

export const PriorityTagIcon = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 16px;
    height: 16px;
`;

export const PriorityTagLabel = styled.span`
    white-space: nowrap;
`;

export const UserAvatar = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    background: ${Variables.ThemeLight.PrimaryInverted9};
    border: 2px solid ${Variables.Swatches.White};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Variables.ThemeLight.PrimaryInverted2};
    font-size: 12px;
    cursor: default;
    flex-shrink: 0;
`;

export const UserAvatarLogo = styled.img`
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    object-fit: cover;
`;

export const UserAvatarLogoContainer = styled.div`
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: default;
    flex-shrink: 0;
`;

export const UnreadDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background: #cbd5e1;
    flex-shrink: 0;
`;

export const CommentBadge = styled.span`
    padding: 2px 6px;
    border-radius: 4px;
    background: #f1f5f9;
    color: #94a3b8;
    font-size: 12px;
    font-weight: 500;
    margin-left: 8px;
`;

export const FromCellContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

// Styled wrapper for InfiniteScrollDataTable with Figma design
export const MessagingTable = styled(InfiniteScrollDataTable)`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: ${Variables.ThemeLight.Background2};
    
    // Table container - remove default borders and enable scrolling
    ${DataTableContainer} {
        border: none;
        box-shadow: none;
        border-radius: 0;
        flex: 1;
        overflow: auto;
        min-height: 0;
    }
    
    // Table headers
    ${DataTableHead} {
        background: ${Variables.Swatches.Gray1};
        
        ${DataTableHeader} {
            background: ${Variables.Swatches.Gray1} !important;
            border-top: none !important;
            border-bottom: 1px solid #e2e8f0 !important;
            border-right: none !important;
            color: #64748b;
            font-size: 12px;
            padding: 16px;
            
            // Align header content (text + icons)
            > div {
                display: flex;
                align-items: center;
                gap: 4px;
            }
            
            &.table-header-with-filter {
                // Sorted column background
                &[data-sorted="true"] {
                    background: ${Variables.Swatches.Gray1} !important;
                }
            }
        }
    }
    
    // Table body
    ${DataTableBody} {
        ${DataTableRow} {
            background: linear-gradient(
                90deg, 
                ${Variables.Swatches.AlphaMiddleGray10} 0%, 
                ${Variables.Swatches.AlphaMiddleGray10} 100%
            ),
            linear-gradient(
                90deg, 
                ${Variables.Swatches.Gray1} 0%, 
                ${Variables.Swatches.Gray1} 100%
            );
            border-bottom: 1px solid #e2e8f0;
            
            &.unread-message {
                background: white;
            }
            
            &:hover {
                background: linear-gradient(
                    90deg, 
                    ${Variables.Swatches.AlphaMiddleGray20} 0%, 
                    ${Variables.Swatches.AlphaMiddleGray20} 100%
                ),
                linear-gradient(
                    90deg, 
                    ${Variables.Swatches.Gray1} 0%, 
                    ${Variables.Swatches.Gray1} 100%
                );
            }
            
            &.unread-message:hover {
                background: ${Variables.Swatches.Gray1};
            }
            
            /* Selected row styling - darker background (must come after other states for proper override) */
            &.selected-message {
                background: ${Variables.Swatches.AlphaMiddleGray30} !important;
            }
            
            /* Keep selected row dark even on hover */
            &.selected-message:hover {
                background: ${Variables.Swatches.AlphaMiddleGray30} !important;
            }
        }
        
        ${DataTableCell} {
            padding: 16px;
            color: #334155;
            font-size: 14px;
            border-right: none;
        }
    }
    
`;
